/**
 * Media completeness / "have vs still need" (visual-cms.md §5) — the health model.
 *
 * DERIVED, never stored (no migration): a per-type key-field set scores each media item on the
 * metadata that matters for SEO / findability, so video-SEO gaps show at a glance. Pure +
 * deterministic so the same scorer drives the card meter (Slice 1), the drawer bar (Slice 2), the
 * spreadsheet (Slice 3) and the "Needs attention" filter (Slice 4). Field sets are deliberately
 * easy to adjust (§5 — they'll tune per client type); keep them here as the single source.
 *
 * The fields live in two places on a media row: the typed columns (`alt`, `caption`) and the
 * `metadata` JSONB bag (`description`, `category`, `tags`, `transcript` — the per-video PACK keys,
 * media/manifest.ts). `has()` knows which is which.
 */

/** The minimal shape the scorer needs — a subset of the media row (kind + the scored fields). */
export interface ScorableMedia {
  kind: 'image' | 'video';
  alt?: string | null;
  caption?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface HealthField {
  /** canonical key — a row column (`alt`/`caption`) or a `metadata` JSONB key. */
  key: 'alt' | 'caption' | 'description' | 'category' | 'tags' | 'transcript';
  /** designer-facing label (chips + drawer). */
  label: string;
}

// Per-type key-field sets (visual-cms.md §5). Order = the pip order on the card.
const VIDEO_FIELDS: HealthField[] = [
  { key: 'description', label: 'Description' },
  { key: 'tags', label: 'Tags' },
  { key: 'category', label: 'Category' },
  { key: 'transcript', label: 'Transcript' },
  { key: 'alt', label: 'Alt text' },
];
const IMAGE_FIELDS: HealthField[] = [
  { key: 'alt', label: 'Alt text' },
  { key: 'caption', label: 'Caption' },
  { key: 'tags', label: 'Tags' },
];

/** The ordered key-field set scored for this media kind. */
export function healthFieldsFor(kind: 'image' | 'video'): HealthField[] {
  return kind === 'video' ? VIDEO_FIELDS : IMAGE_FIELDS;
}

/** Is this scored field actually filled? `tags` is a non-empty array in metadata; the string
 *  fields are non-blank (row columns for alt/caption, metadata keys otherwise). */
export function hasField(item: ScorableMedia, key: HealthField['key']): boolean {
  if (key === 'alt') return !!(item.alt && item.alt.trim());
  if (key === 'caption') return !!(item.caption && item.caption.trim());
  const meta = item.metadata ?? {};
  if (key === 'tags') return Array.isArray(meta.tags) && meta.tags.length > 0;
  const v = meta[key];
  return typeof v === 'string' && v.trim().length > 0;
}

export interface HealthScore {
  filled: number;
  total: number;
  /** the still-missing fields, in set order (for the "+ X" chips + the "still needs …" line). */
  missing: HealthField[];
  /** 0–100, for the drawer bar. */
  pct: number;
}

/** Score a media item against its type's key-field set. Pure. */
export function scoreMedia(item: ScorableMedia): HealthScore {
  const fields = healthFieldsFor(item.kind);
  const missing = fields.filter((f) => !hasField(item, f.key));
  const filled = fields.length - missing.length;
  return { filled, total: fields.length, missing, pct: fields.length ? Math.round((100 * filled) / fields.length) : 0 };
}
